import React from "react";
import type { Client, FollowUpStatusItem } from "../../../types";
import { displayPhoneNumber } from "../../../utils/phoneUtils";
import { maskCpfForDisplay } from "../../../utils/cpfUtils";
import { isBirthdayToday } from "../../../utils/dateUtils";
import { Avatar, GridAction, DataGrid, Icons } from "../../shared";
import {
  sendWhatsAppMessage,
  WHATSAPP_ICON_PATH,
} from "../../../utils/whatsappUtils";
import { CarExpiredFollowUpReminder } from "../AdminAppointments/CarExpiredFollowUpReminder";

interface ClientsGridViewProps {
  clients: Client[];
  onEdit: (client: Client) => void;
  onDelete: (clientId: number) => void;
  onViewCars: (clientId: number) => void;
  onViewDetails: (clientId: number) => void;
  companyCategory?: "car" | "barbershop";
  expiredByClientId?: Map<number, FollowUpStatusItem[]>;
  onExpiredScheduled?: () => void;
}

export const ClientsGridView: React.FC<ClientsGridViewProps> = ({
  clients,
  onEdit,
  onDelete,
  onViewCars,
  onViewDetails,
  companyCategory,
  expiredByClientId,
  onExpiredScheduled,
}) => {
  const formatLastAppointment = (dateString?: string) => {
    if (!dateString) return "Nunca";
    try {
      return new Date(dateString).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const renderClientCard = (client: Client) => (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden hover:shadow-md transition-all duration-300 transform hover:scale-[1.02]">
      {/* Card Header */}
      <div className="p-6 border-b border-gray-200 dark:border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Avatar text={client.name} size="md" variant="blue" subtle />
            <div className="ml-3 flex items-center gap-2 min-w-0">
              <h3
                className="font-bold text-gray-900 mb-0 cursor-pointer truncate"
                onClick={() => onViewDetails(client.id)}
              >
                {client.name}
              </h3>
              {isBirthdayToday(client.birthday) && (
                <span className="text-yellow-500 text-lg" title="Aniversário hoje!">
                  🎂
                </span>
              )}
              <CarExpiredFollowUpReminder
                items={expiredByClientId?.get(client.id) ?? []}
                clientId={client.id}
                clientName={client.name}
                clientPhone={client.phoneNumber}
                onScheduled={onExpiredScheduled}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-6 space-y-4">
        {/* Contact Info */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-blue-500">📞</span>
            <span className="text-sm text-gray-900 font-medium">
              {displayPhoneNumber(client.phoneNumber)}
            </span>
          </div>
          {client.address && (
            <div className="flex items-start gap-3">
              <span className="text-blue-500">📍</span>
              <span className="text-sm text-gray-600 line-clamp-2">
                {client.address}
              </span>
            </div>
          )}
          {client.cpf && (
            <div className="flex items-center gap-3">
              <Icons.Badge sx={{ fontSize: 18, color: "#3b82f6" }} titleAccess="CPF" />
              <span className="text-sm text-gray-600 tracking-wide">
                {maskCpfForDisplay(client.cpf)}
              </span>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <div className="text-center">
            <div className="text-lg font-bold text-blue-600">
              {client.appointmentCount}
            </div>
            <div className="text-xs text-gray-500">Agendamentos</div>
          </div>
          <div className="text-center">
            <div className="text-sm font-medium text-gray-900">
              {formatLastAppointment(client.lastAppointment?.appointmentDate)}
            </div>
            <div className="text-xs text-gray-500">Último agendamento</div>
          </div>
        </div>
      </div>

      {/* Card Actions */}
      <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-100">
        <div className="text-xs text-gray-500">
          Cliente desde{" "}
          {new Date(client.createdAt).toLocaleDateString("pt-BR", {
            month: "short",
            year: "numeric",
          })}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() =>
              sendWhatsAppMessage({
                phoneNumber: client.phoneNumber,
                message: '',
              })
            }
            className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-all duration-200"
            title="Enviar mensagem via WhatsApp"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d={WHATSAPP_ICON_PATH} />
            </svg>
          </button>
          {companyCategory === "car" && (
            <button
              onClick={() => onViewCars(client.id)}
              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200"
              title="Gerenciar veículos"
            >
              <Icons.Car sx={{ fontSize: 18 }} />
            </button>
          )}
          <button
            onClick={() => onEdit(client)}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
            title="Editar cliente"
          >
            <Icons.Edit sx={{ fontSize: 18 }} />
          </button>
          <button
            onClick={() => onDelete(client.id)}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
            title="Excluir cliente"
          >
            <Icons.Delete sx={{ fontSize: 18 }} />
          </button>
        </div>
      </div>
    </div>
  );

  const actions: GridAction<Client>[] = [
    ...(companyCategory === "car"
      ? [
          {
            label: "Gerenciar veículos",
            icon: <Icons.Car sx={{ fontSize: 18 }} />,
            onClick: (client: Client) => onViewCars(client.id),
            className:
              "p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200",
            title: "Gerenciar veículos",
          },
        ]
      : []),
    {
      label: "Editar cliente",
      icon: <Icons.Edit sx={{ fontSize: 18 }} />,
      onClick: (client) => onEdit(client),
      className:
        "p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200",
      title: "Editar cliente",
    },
    {
      label: "Excluir cliente",
      icon: <Icons.Delete sx={{ fontSize: 18 }} />,
      onClick: (client) => onDelete(client.id),
      className:
        "p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200",
      title: "Excluir cliente",
    },
  ];

  return (
    <DataGrid<Client>
      data={clients}
      renderCard={renderClientCard}
      actions={actions}
      keyExtractor={(client) => client.id}
      emptyMessage="Nenhum cliente encontrado"
      containerClassName="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
    />
  );
};
